/**
 * Widget Renderer Component
 *
 * Maps widget IDs to their respective components and renders them.
 * Provides fallback for unimplemented widgets.
 *
 * @author BizClick WMS
 * @version 1.0.0
 */

import React from 'react';
import { AlertCircle } from 'lucide-react';
import { WidgetInstance } from '../types';
import { getWidgetById } from '../widgetRegistry';

// Import all widget components
import {
  ClockWidget,
  OrderSummaryWidget,
  PickingStatsWidget,
  ShippingStatsWidget,
  KPIGridWidget,
  ActivityFeedWidget,
  OperatorStatusWidget,
  InventoryAlertsWidget,
  HourlyActivityChartWidget,
} from './widgets';

interface WidgetRendererProps {
  instance: WidgetInstance;
}

/**
 * Map of widget IDs to their components
 */
const WIDGET_COMPONENTS: Record<string, React.FC<{ instance: WidgetInstance }>> = {
  // System
  'clock': ClockWidget,

  // Orders
  'order-summary': OrderSummaryWidget,
  'orders-pending': OrderSummaryWidget, // Reuse with different config

  // Picking
  'picking-stats': PickingStatsWidget,
  'picking-progress': PickingStatsWidget,
  'active-pickers': PickingStatsWidget,

  // Shipping
  'shipping-stats': ShippingStatsWidget,
  'ready-to-ship': ShippingStatsWidget,

  // KPI
  'kpi-grid': KPIGridWidget,
  'pick-efficiency': KPIGridWidget,
  'on-time-delivery': KPIGridWidget,

  // Activity
  'activity-feed': ActivityFeedWidget,
  'hourly-activity-chart': HourlyActivityChartWidget,

  // Operators
  'operator-status': OperatorStatusWidget,
  'top-performers': OperatorStatusWidget,

  // Inventory
  'inventory-alerts': InventoryAlertsWidget,
  'warehouse-capacity': InventoryAlertsWidget,
};

/**
 * Fallback component for unimplemented widgets
 */
const FallbackWidget: React.FC<{ instance: WidgetInstance }> = ({ instance }) => {
  const widgetDef = getWidgetById(instance.widgetId);

  return (
    <div className="h-full w-full bg-gray-800 rounded-lg border-2 border-gray-700 flex flex-col items-center justify-center p-4">
      <AlertCircle className="w-8 h-8 text-gray-500 mb-2" />
      <p className="text-sm text-gray-400 text-center">
        Widget ikke implementert
      </p>
      <p className="text-xs text-gray-500 mt-1">
        {widgetDef?.name || instance.widgetId}
      </p>
    </div>
  );
};

/**
 * Widget Renderer Component
 */
const WidgetRenderer: React.FC<WidgetRendererProps> = ({ instance }) => {
  const WidgetComponent = WIDGET_COMPONENTS[instance.widgetId] || FallbackWidget;

  return <WidgetComponent instance={instance} />;
};

export default WidgetRenderer;
